import { withQuotientGrowthIllustration } from '@/adapters/primitive/_pure/derivation/natural/quotientGrowthIllustration';
import { configForPrimitiveTrace, primitiveTraceSettings, projectPrimitiveTracePayload } from '@/adapters/primitive/_pure/derivation/primitiveSteps';
import { resolvePrimitiveOperationInput } from '@/adapters/primitive/_pure/interaction/operationInput';
import { derivePrimitiveTracePayload } from '@/adapters/primitive/_pure/model/trace';
import type { Config, PrimitiveWheelState, RuntimeState } from '@/adapters/primitive/_contract/config';
import type { PrimitiveStepState } from '@/adapters/primitive/_contract/steps';
import type { PrimitiveTracePayload } from '@/adapters/primitive/_contract/trace';

export function createPrimitiveStepResolver() {
    let semanticCache: { key: string; payload: PrimitiveTracePayload } | undefined;
    let projectionCache: { config: Config; payload: PrimitiveTracePayload;
        directions?: RuntimeState['sceneDirectionCatalogs'];
        splitAlignmentApproach: boolean; divisionComparison?: 'explicit' | 'integrated'; illustrativeQuotientBands?: number;
        fullComparisons?: number;
        steps: readonly PrimitiveStepState[] } | undefined;
    let conceptCache: { config: Config; key: string; steps: readonly PrimitiveStepState[] } | undefined;
    return (config: Config, state: RuntimeState): readonly PrimitiveStepState[] => {
        if (state.conceptArrangement) {
            semanticCache = undefined;
            projectionCache = undefined;
            const arrangement = state.conceptArrangement;
            const payloadKey = JSON.stringify(arrangement);
            if (conceptCache?.config !== config || conceptCache.key !== payloadKey) {
                const naturalWheel = (role: 'identity-input' | 'identity-output', value: number,
                    powers: readonly number[], position: [number, number, number]) => ({
                    role, valueKind: 'natural' as const, value, powers, position, roleless: true,
                    countedThroughDigitByPower: Object.fromEntries(powers.map((power) => [power,
                        Math.floor(value / 10 ** power) % 10])),
                });
                const booleanWheel = (role: 'identity-input' | 'identity-output', value: boolean,
                    position: [number, number, number]) => ({
                    role, valueKind: 'boolean' as const, value, powers: [0], position, roleless: true,
                });
                const focusedPowers = arrangement.arrangementKind === 'natural-wheel' ? arrangement.focusedPowers : undefined;
                const assemblyWheel = (bandKind: 'unit' | 'radix', power: number): PrimitiveWheelState => {
                    const powers = Array.from({ length: power + 1 }, (_, index) => index);
                    return {
                        ...naturalWheel(bandKind === 'unit' ? 'identity-input' : 'identity-output',
                            (bandKind === 'unit' ? 1 : 10) * 10 ** power, powers,
                            [arrangement.arrangementKind === 'natural-bands' && arrangement.bandKind === 'equivalence'
                                ? (bandKind === 'unit' ? -1 : 1) * config.base.display.wheel.wheelGap / 2 : 0, 0, 0]),
                        countedDigitsByPower: Object.fromEntries(powers.map(index => [index,
                            index !== power ? [] : bandKind === 'unit' ? [1] : Array.from({ length: 10 }, (_, digit) => digit)])),
                        bandOpacityByPower: Object.fromEntries(powers.map(index =>
                            [index, index === power ? 1 : config.natural.display.band.spentOpacity])),
                        hubOpacity: config.natural.display.band.spentOpacity,
                        bandLabels: arrangement.arrangementKind === 'natural-bands' && arrangement.bandLabels
                            ? arrangement.bandLabels : { radial: true, angular: false },
                    };
                };
                const wheels: PrimitiveWheelState[] = arrangement.arrangementKind === 'natural-wheel'
                    ? [{ ...naturalWheel('identity-input', arrangement.value, arrangement.powers, [0, 0, 0]),
                        ...(arrangement.bandLabels === undefined ? {} : { bandLabels: arrangement.bandLabels }),
                        ...(focusedPowers === undefined ? {} : {
                            hubOpacity: config.natural.display.band.spentOpacity,
                            bandOpacityByPower: Object.fromEntries(arrangement.powers.map(power => [
                                power, focusedPowers.includes(power) ? 1 : config.natural.display.band.spentOpacity,
                            ])),
                        }) }]
                    : arrangement.arrangementKind === 'boolean-wheel'
                        ? [booleanWheel('identity-input', arrangement.value, [0, 0, 0])]
                        : arrangement.arrangementKind === 'primitive-overview'
                            ? [
                                naturalWheel('identity-input', arrangement.naturalValue, arrangement.naturalPowers,
                                    [-config.base.display.wheel.wheelGap / 2, 0, 0]),
                                booleanWheel('identity-output', arrangement.booleanValue,
                                    [config.base.display.wheel.wheelGap / 2, 0, 0]),
                            ]
                            : [
                                ...(arrangement.bandKind === 'radix' ? [] : [assemblyWheel('unit',
                                    arrangement.power + (arrangement.bandKind === 'equivalence' ? 1 : 0))]),
                                ...(arrangement.bandKind === 'unit' ? [] : [assemblyWheel('radix', arrangement.power)]),
                            ];
                conceptCache = { config, key: payloadKey, steps: [{
                    stepKey: 'concept', label: 'Concept', description: 'Authored concept arrangement',
                    cameraFocus: { stationKey: 'overview', viewKey: 'overview',
                        roles: arrangement.arrangementKind === 'primitive-overview' || arrangement.arrangementKind === 'natural-bands'
                            ? ['identity-input', 'identity-output'] : ['identity-input'] },
                    keyframes: [{ transition: 'instant', events: [], wheels }],
                }] };
            }
            return conceptCache.steps;
        }
        conceptCache = undefined;
        let payload = state.selectedTrace?.payload;
        if (!payload) {
            const input = resolvePrimitiveOperationInput(state.inputDraft);
            if (!input) {
                semanticCache = undefined;
                projectionCache = undefined;
                return [];
            }
            const settings = primitiveTraceSettings(config, { allowExoticTerminalStates: true });
            const key = JSON.stringify({ input, settings });
            if (semanticCache?.key !== key) semanticCache = { key, payload: derivePrimitiveTracePayload(input, settings) };
            payload = semanticCache.payload;
        } else {
            semanticCache = undefined;
        }
        const splitAlignmentApproach = Boolean(state.episode);
        const divisionComparison = state.episode ? state.divisionComparison : undefined;
        const illustrativeQuotientBands = state.episode ? state.illustrativeQuotientBands : undefined;
        const fullComparisons = state.episode ? state.fullComparisons : undefined;
        const directions = state.episode ? state.sceneDirectionCatalogs : undefined;
        if (projectionCache?.config !== config || projectionCache.payload !== payload
            || projectionCache.splitAlignmentApproach !== splitAlignmentApproach
            || projectionCache.divisionComparison !== divisionComparison
            || projectionCache.fullComparisons !== fullComparisons
            || projectionCache.illustrativeQuotientBands !== illustrativeQuotientBands
            || projectionCache.directions !== directions) {
            const effective = configForPrimitiveTrace(config, state.selectedTrace);
            projectionCache = { config, payload, splitAlignmentApproach, divisionComparison, illustrativeQuotientBands, directions,
                fullComparisons,
                steps: withQuotientGrowthIllustration(effective, projectPrimitiveTracePayload(effective, payload,
                    splitAlignmentApproach, divisionComparison, directions?.visuals, fullComparisons), illustrativeQuotientBands,
                fullComparisons) };
        }
        return projectionCache.steps;
    };
}
